import { useEffect, useState } from "react";
import { Ban } from "lucide-react";

export function InvoiceVisual() {
  const FROM = 5000;
  const TO = 8000;
  const DURATION = 4000;
  const PAUSE = 3000;
  const PULSE = 500;
  const [value, setValue] = useState(FROM);
  const [pulsing, setPulsing] = useState(false);

  useEffect(() => {
    let raf: number;
    let timeout: ReturnType<typeof setTimeout>;
    let pulseTimeout: ReturnType<typeof setTimeout>;
    let start: number | null = null;
    const tick = (t: number) => {
      if (start === null) start = t;
      const p = Math.min(1, (t - start) / DURATION);
      setValue(Math.round(FROM + (TO - FROM) * p));
      if (p < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        setPulsing(true);
        pulseTimeout = setTimeout(() => setPulsing(false), PULSE);
        timeout = setTimeout(() => {
          start = null;
          setValue(FROM);
          raf = requestAnimationFrame(tick);
        }, PAUSE);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timeout);
      clearTimeout(pulseTimeout);
    };
  }, []);

  return (
    <div className="relative flex w-full items-center justify-center px-6">
      <div className="w-full max-w-[180px] rounded-md border border-border/70 bg-background/80 p-3 shadow-sm">
        <div className="flex items-center justify-between">
          <span className="text-[9px] font-semibold tracking-[0.18em] text-muted-foreground">
            INVOICE
          </span>
          <Ban className="h-3.5 w-3.5 text-brand-red" aria-hidden />
        </div>
        <div className="mt-2 space-y-1.5">
          <div className="h-1.5 w-3/4 rounded bg-muted" />
          <div className="h-1.5 w-1/2 rounded bg-muted" />
        </div>
        <div className="mt-3 border-t border-border/60 pt-2 text-right">
          <div
            className={`text-base font-bold text-brand-red tabular-nums inline-block origin-right transition-transform duration-300 ease-out ${pulsing ? "scale-125" : "scale-100"}`}
          >
            ${value.toLocaleString()}
          </div>
        </div>
      </div>
    </div>
  );
}

export function ChartVisual() {
  return (
    <svg viewBox="0 0 200 100" className="h-full w-full px-6" aria-hidden role="img">
      <defs>
        <linearGradient id="chart-fade" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="oklch(0.68 0.21 25)" stopOpacity="0.35" />
          <stop offset="100%" stopColor="oklch(0.68 0.21 25)" stopOpacity="0" />
        </linearGradient>
      </defs>
      <g stroke="currentColor" strokeOpacity="0.12">
        <line x1="10" y1="20" x2="190" y2="20" />
        <line x1="10" y1="50" x2="190" y2="50" />
        <line x1="10" y1="80" x2="190" y2="80" />
      </g>
      <path d="M10,30 L50,38 L90,55 L130,68 L170,82 L190,86 L190,95 L10,95 Z" fill="url(#chart-fade)" />
      <path id="chart-line" d="M10,30 L50,38 L90,55 L130,68 L170,82 L190,86" fill="none" stroke="oklch(0.68 0.21 25)" strokeWidth="2" strokeLinecap="round" />
      <circle r="4" fill="oklch(0.68 0.21 25)">
        <animateMotion dur="6s" repeatCount="indefinite" rotate="auto" keyPoints="0;1" keyTimes="0;1" calcMode="linear">
          <mpath href="#chart-line" />
        </animateMotion>
        <animate attributeName="r" values="3;6;3" dur="1.8s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="1;0.5;1" dur="1.8s" repeatCount="indefinite" />
      </circle>
      <circle r="4" fill="oklch(0.68 0.21 25)" opacity="0.35">
        <animateMotion dur="6s" repeatCount="indefinite" rotate="auto">
          <mpath href="#chart-line" />
        </animateMotion>
        <animate attributeName="r" values="4;14;4" dur="1.8s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.5;0;0.5" dur="1.8s" repeatCount="indefinite" />
      </circle>
    </svg>
  );
}

export function LockVisual() {
  return (
    <div className="relative flex w-full items-center justify-center">
      <svg viewBox="0 0 120 100" className="h-44 w-auto scale-125" aria-hidden role="img">
        <g stroke="currentColor" strokeOpacity="0.35" strokeWidth="3" fill="none">
          <ellipse cx="20" cy="50" rx="10" ry="6" />
          <ellipse cx="38" cy="50" rx="10" ry="6" />
          <ellipse cx="82" cy="50" rx="10" ry="6" />
          <ellipse cx="100" cy="50" rx="10" ry="6" />
        </g>
        <g style={{ transformOrigin: "60px 52px", animation: "lock-wobble 2.4s ease-in-out infinite" }}>
          <rect x="48" y="40" width="24" height="24" rx="4" fill="oklch(0.68 0.21 25)" fillOpacity="0.15" stroke="oklch(0.68 0.21 25)" strokeWidth="2" />
          <path d="M52,40 V34 a8,8 0 0 1 16,0 V40" fill="none" stroke="oklch(0.68 0.21 25)" strokeWidth="2" />
          <circle cx="60" cy="50" r="2" fill="oklch(0.68 0.21 25)" />
          <rect x="59" y="50" width="2" height="6" fill="oklch(0.68 0.21 25)" />
        </g>
        <style>{`@keyframes lock-wobble { 0%,100% { transform: rotate(0deg);} 25% { transform: rotate(-3deg);} 75% { transform: rotate(3deg);} }`}</style>
      </svg>
    </div>
  );
}
